import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import IconButton, {
  IconButtonSizes,
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import { COUNTER_MONITORING_SIDEBAR_IS_COLLAPSED } from '@lib/enums/localStorageKeys';
import classNames from 'classnames';
import styles from './DashboardContentWithRightSidebar.module.scss';

/**
 * The `.sidebar` column is `position: absolute; top: 0; bottom: 0` against
 * this component's own root, so it relies on the outer page NOT scrolling
 * independently. Every consuming app's `DashboardLayout` exposes a
 * `disableContentScroll` route list for exactly this — when you add a new
 * route/tab that renders this component with a visible sidebar
 * (`withoutSidebar` false/omitted), add its path there too, or the outer
 * page and this sidebar will scroll independently and the sidebar will
 * misbehave. See `disableContentScroll` in
 * `apps/admin/src/layouts/DashboardLayout/DashboardLayout.tsx`.
 */
interface Props {
  children: React.ReactNode;
  sidebar: React.ReactNode;
  sidebarHeader?: React.ReactNode;
  toggleBtnTop?: number;
  useSidebarHeader?: boolean;
  /**
   * Render the content column full width, without the sidebar. Lets a view
   * whose sidebar comes and goes (e.g. per tab) keep this layout mounted
   * instead of swapping it out, which would remount the whole content subtree.
   */
  withoutSidebar?: boolean;
}

const isCollapsedFromStorage = localStorage.getItem(
  COUNTER_MONITORING_SIDEBAR_IS_COLLAPSED,
);

function DashboardContentWithRightSidebar({
  children,
  sidebar,
  sidebarHeader,
  toggleBtnTop,
  useSidebarHeader,
  withoutSidebar,
}: Props) {
  const { t } = useTranslation();
  const isCollapsedValueFromStorage = isCollapsedFromStorage
    ? JSON.parse(isCollapsedFromStorage)
    : false;
  const [isCollapsed, setCollapsed] = useState(isCollapsedValueFromStorage);
  const handleExpanded = () => {
    setCollapsed((prevState: boolean) => !prevState);
  };

  useEffect(() => {
    localStorage.setItem(
      COUNTER_MONITORING_SIDEBAR_IS_COLLAPSED,
      JSON.stringify(isCollapsed),
    );
  }, [isCollapsed]);

  return (
    <div
      className={classNames(styles.root, {
        [styles.rootCollapsed]: !withoutSidebar && isCollapsed,
        [styles.rootWithoutSidebar]: withoutSidebar,
      })}
    >
      <div className={styles.content}>{children}</div>
      {!withoutSidebar && (
        <div
          className={classNames(styles.sidebar, {
            [styles.sidebarWithHeader]: useSidebarHeader,
          })}
        >
          <div className={styles.sidebarWrap}>
            <div
              className={styles.toggleBtn}
              style={toggleBtnTop ? { top: toggleBtnTop } : undefined}
            >
              <Tooltip
                body={t(isCollapsed ? 'expand' : 'collapse')}
                key={isCollapsed ? 'expand' : 'collapse'}
                placement="left"
              >
                <IconButton
                  icon={isCollapsed ? 'chevron_left' : 'chevron_right'}
                  onClick={handleExpanded}
                  rounded
                  type={IconButtonTypes.SecondaryOutlined}
                  size={IconButtonSizes.Small}
                />
              </Tooltip>
            </div>
            <div className={styles.sidebarContentWrap}>
              <div className={styles.sidebarContent}>
                {useSidebarHeader && (
                  <div className={styles.sidebarHeader}>{sidebarHeader}</div>
                )}
                {sidebar}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardContentWithRightSidebar;
